import { TextResources } from "@portal/assets/text/TextResources";
import Typography from "@portal/components/atoms/v2/typography";
import { FC, useState } from "react";
import { handleKeyDownAtInteger } from "@portal/helpers/handleKeyEvents";
import { ICreateAgreementFormDataOpen, ICreateSimpleAgreementFormOpen } from "./types";
import { useRouter } from "next/navigation";
import { simpleFunds } from "../simpleCreateAgreement";
import Input from "@portal/components/atoms/v2/input";
import { Field, Label } from "@headlessui/react";
import Button from "@portal/components/atoms/v2/buttons/button";
import useBankFundCosts from "@portal/hooks/useBankFundCosts";
import useFund from "@portal/hooks/useFund";
import CreateSimpleAgreementFooter from "../simpleCreateAgreement/footer";
import CreateSimpleAgreementHeader from "../simpleCreateAgreement/header";
import { formatCurrency } from "@portal/utils/numberFormatters";
import PortalPageBox from "@portal/components/atoms/portalPageBox";

const SimpleCreateAgreementOpen: FC<ICreateSimpleAgreementFormOpen> = ({ isin, monthlyDeposit, oneTimeDeposit, centerId }) => {
  const { getFee } = useBankFundCosts(centerId);

  const simpleFund = simpleFunds.find(f => (isin ? f.isin === isin : f.isin === "NO0010867682"));
  const { data: fund } = useFund(simpleFund?.isin);

  const nettoFeePercent = fund ? fund.feePercent - fund.refundProvisionPercent : 0;
  const fee = getFee(fund?.custodyFeeType ?? "");
  const customerPrice = nettoFeePercent + (fee ?? 0);

  const router = useRouter();
  const [loading, setLoading] = useState<boolean>(false);

  const [formData, setFormData] = useState<ICreateAgreementFormDataOpen>({
    monthlyAmount: monthlyDeposit ?? "",
    oneTimeAmount: oneTimeDeposit ?? "",
  });

  const [monthlyAmountError, setMonthlyAmountError] = useState<string>("");

  const handleSubmitForm = async () => {
    setLoading(true);
    const monthlyAmountError = validateMonthlyAmount(formData.monthlyAmount);
    if (monthlyAmountError) {
      setLoading(false);
      return;
    } else {
      router.push(
        `/createAgreement/simple?centerId=${centerId}&isin=${simpleFund ? simpleFund.isin : "NO0010867682"}&oneTimeDeposit=${
          formData.oneTimeAmount
        }&monthlyDeposit=${formData.monthlyAmount}`,
      );
      setLoading(false);
    }
  };

  const validateMonthlyAmount = (amount?: string) => {
    let error = "";
    if (!amount || Number(amount) < 250) {
      error = TextResources.CREATE_AGREEMENT_SIMPLE_MONTHLY_AMOUNT_ERROR;
    }
    setMonthlyAmountError(error);
    return error;
  };

  if (!simpleFund) {
    return (
      <div className="flex flex-col max-w-3xl w-full mb-4">
        <PortalPageBox className="flex flex-col space-y-3">
          <div className="space-y-2">
            <Typography variant="H4" className="text-center">
              {TextResources.CREATE_AGREEMENT_SIMPLE_TITLE}
            </Typography>
            <Typography variant="Body" className="text-center">
              {TextResources.CREATE_AGREEMENT_SIMPLE_FUND_NOT_FOUND}
            </Typography>
          </div>
        </PortalPageBox>
      </div>
    );
  }

  return (
    <div className="flex flex-col max-w-3xl w-full mb-4">
      <PortalPageBox className="flex flex-col space-y-3">
        <CreateSimpleAgreementHeader fund={simpleFund} />
        <div className="space-y-4 w-72 sm:w-96 mx-auto">
          <Field>
            <Label>
              <Typography variant="Bold">{TextResources.CREATE_AGREEMENT_SIMPLE_ONE_TIME_AMOUNT_LABEL}</Typography>
            </Label>
            <Input
              className="text-right"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const removeSpaces = e.target.value.replace(/\s/g, "");
                setFormData({
                  ...formData,
                  oneTimeAmount: formatCurrency(Number(removeSpaces)),
                });
              }}
              value={formData.oneTimeAmount}
              name="oneTimeAmount"
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                handleKeyDownAtInteger(e);
              }}
            />
          </Field>
          <Field>
            <Label>
              <Typography variant="Bold">{TextResources.CREATE_AGREEMENT_SIMPLE_MONTHLY_AMOUNT_LABEL}</Typography>
            </Label>
            <Input
              className="text-right"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const removeSpaces = e.target.value.replace(/\s/g, "");
                setFormData({
                  ...formData,
                  monthlyAmount: formatCurrency(Number(removeSpaces)),
                });
              }}
              onBlur={() => validateMonthlyAmount(formData.monthlyAmount)}
              value={formData.monthlyAmount}
              name="monthlyAmount"
              error={!!monthlyAmountError}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                handleKeyDownAtInteger(e);
              }}
            />
            {monthlyAmountError && <Typography className="text-red">{monthlyAmountError}</Typography>}
          </Field>
          <div className="pt-2">
            <Button
              text={TextResources.CREATE_AGREEMENT_SIMPLE_OPEN_BUTTON_TEXT}
              onClick={handleSubmitForm}
              wide
              loading={loading ? 1 : 0}
            />
          </div>
        </div>
        <CreateSimpleAgreementFooter fund={simpleFund} customerPrice={customerPrice} />
      </PortalPageBox>
    </div>
  );
};

export default SimpleCreateAgreementOpen;
